import { useEffect, useMemo } from "react";

import { useNorthbeamLazyQuery } from "@hooks/http";
import { type FetchClientSettingsForCycle } from "@north-beam/nb-client-side";
import { MMMClientState, type MMMClientCycle } from "@north-beam/nb-common";
import { CLIENT_SETTINGS_FOR_CYCLE_QUERY } from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";
import { useAdoptedMix } from "@pages/mmm/hooks/use-adopted-mix";
import { useBudgetMixes } from "@pages/mmm/hooks/use-budget-mixes";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";

import { type ClientSetting } from "../components/explore-budgets/client-setting/types";

export function useLoadClientSettings(clientCycle: MMMClientCycle) {
  const { setClientSettings } = useClientSettings();

  const {
    loadData: loadClientSettings,
    data: { data: clientSettingsData, loading: loadingClientSettings },
  } = useNorthbeamLazyQuery<FetchClientSettingsForCycle>(
    CLIENT_SETTINGS_FOR_CYCLE_QUERY,
  );

  const shouldHaveClientSettings = useMemo(() => {
    return [
      MMMClientState.ADOPTING_MIX,
      MMMClientState.PREPARING_MIX,
      MMMClientState.TRACKING_MIX,
      MMMClientState.REVIEWING_PERIOD,
    ].includes(clientCycle.state);
  }, [clientCycle.state]);

  const shouldQueryClientSettings = useMemo(() => {
    return (
      (clientCycle.id && shouldHaveClientSettings) ||
      clientCycle.state === MMMClientState.CREATING_SETTINGS
    );
  }, [shouldHaveClientSettings, clientCycle.state, clientCycle.id]);

  useEffect(() => {
    if (shouldQueryClientSettings) {
      loadClientSettings({
        variables: {
          id: clientCycle.id,
        },
      });
    } else if (!shouldQueryClientSettings) {
      setClientSettings([]);
    }
  }, [
    clientCycle.id,
    shouldQueryClientSettings,
    loadClientSettings,
    setClientSettings,
  ]);

  useEffect(() => {
    // TODO: Fix typing
    if (clientSettingsData) {
      setClientSettings(
        clientSettingsData.me.mmm.cycle
          .clientSettings as unknown as ClientSetting[],
      );
    }
  }, [clientSettingsData, setClientSettings]);

  const [budgetMixes] = useBudgetMixes();
  const [adoptedMixData, setAdoptedMixData] = useAdoptedMix();

  const recommendedData = useMemo(() => {
    if (!budgetMixes) {
      return [];
    }

    return [...budgetMixes].sort((d1, d2) => d1.id - d2.id);
  }, [budgetMixes]);

  useEffect(() => {
    const adoptedMix = recommendedData.find((datum) => datum.is_adopted);
    if (adoptedMix && adoptedMix.id !== adoptedMixData?.id) {
      setAdoptedMixData({
        id: adoptedMix.id,
        clientSettingId: adoptedMix.client_setting_id ?? undefined,
      });
    } else if (!adoptedMix) {
      setAdoptedMixData(null);
    }
  }, [recommendedData, setAdoptedMixData, adoptedMixData?.id]);

  return {
    loading: loadingClientSettings,
  };
}
